import React, { forwardRef, useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router';
import { Toolbar, AppBar, Tab, Tabs, Typography, Stack, Box, Slide, Chip, Button, IconButton, Tooltip } from '@mui/material';
import { KeyboardBackspace, OpenInNew } from '@mui/icons-material';
import { format } from 'date-fns';
import LoadingOverlay from '../loadingOverlay/LoadingOverlay';
import Links from './views/Links';
import Hotspot from './views/Hotspot';
import useApi from '../hooks/useApi';
import useSnackbar from '../hooks/useSnackbar';
import { useTheme } from '@mui/material/styles';

const titleStyle = {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis'
};

const Transition = forwardRef((props, ref) => {
    return <Slide direction="up" ref={ref} {...props} />;
});

Transition.displayName = 'Transition';

const NewPage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { handleGet } = useApi();
    const { showSnackbar } = useSnackbar();
    const [page, setPage] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isInitialising, setIsInitialising] = useState(true);
    const [tab, setTab] = useState(0);
    const [publishedDate, setPublishedDate] = useState('-');
    const theme = useTheme();

    const handleLoadPage = async () => {
        const response = await handleGet(`pages/reports/${id}`);

        if (!response.ok) {
            showSnackbar('Error fetching Page', 'error');
            return;
        }

        setPage(await response.json());
    };

    useEffect(() => {
        if (!isInitialising) {
            setIsLoading(false);
            setPage(null);
        }

        handleLoadPage();
    }, [id]);

    useEffect(() => {
        setIsLoading(!Boolean(page));

        if (!page) {
            return;
        }
        else {
            setPublishedDate(format((new Date(page.publishedDateTime)), 'dd-MM-yyyy HH:mm'));
            setIsInitialising(false);
        }
    }, [page]);

    useEffect(() => {
        handleLoadPage();
    }, []);

    if (isLoading || isInitialising || !page) {
        return (
            <LoadingOverlay />
        );
    }

    return (
        <>
            <AppBar
                position="sticky"
                color="inherit"
                sx={{
                    top: 112,
                    zIndex: theme.zIndex.appBar - 1,
                    borderTop: 1,
                    borderColor: theme.palette.divider
                }}
            >
                <Toolbar variant="dense" sx={{ overflow: 'hidden', pt: 2 }}>
                    <Stack>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Tooltip title="Return to Dashboard">
                                <IconButton onClick={() => navigate(-1)}>
                                    <KeyboardBackspace fontSize="small" />
                                </IconButton>
                            </Tooltip>
                            <Box sx={{ display: 'flex', flexGrow: 1, ml: 1 }}>
                                <Typography component="h1" variant="h6" sx={titleStyle}>{page.name}</Typography>
                            </Box>
                        </Box>
                        <Typography component="h2" variant="body1" sx={titleStyle}>Title: {page.title}</Typography>
                    </Stack>
                </Toolbar>
                <Toolbar variant="dense">
                    <Box sx={{ flexGrow: 1, overflow: 'hidden' }}>
                        {page.published ? (
                            <Typography component="h2" variant="subtitle1" sx={titleStyle}>
                                Published: {publishedDate}
                            </Typography>
                        ) : (
                            <Typography
                                component="h2"
                                variant="subtitle1"
                                sx={{
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    color: theme.palette.text.secondary
                                }}
                            >
                                Last Published: {publishedDate}
                            </Typography>
                        )}
                    </Box>
                    <Box sx={{ whiteSpace: 'nowrap', paddingLeft: 2 }}>
                        <Stack direction="row" spacing={2} sx={{ alignItems: 'center' }}>
                            <Typography>Status: <Chip component="span" color={page.published ? 'success' : undefined} label={page.published ? 'Published' : 'Draft'} /></Typography>
                            <Typography>Total Opens: {page.totalOpens}</Typography>
                            <Typography>Total Clicks: {page.totalClicks}</Typography>
                            <Button
                                href={page.viewLink}
                                target="_blank"
                                endIcon={<OpenInNew />}
                                disabled={!page.published}
                            >
                                Open Page
                            </Button>
                        </Stack>
                    </Box>
                </Toolbar>
                <Tabs value={tab} onChange={(e, tab) => setTab(tab)}>
                    <Tab label="Hotspot" />
                    <Tab label="Links" />
                    <Tab label="Print" disabled={true} />
                </Tabs>
            </AppBar>
            <Box sx={{ overflow: 'hidden' }}>
                {tab === 0 && (
                    <Hotspot
                        html={page.compiledHtml}
                        itemLinks={page.links}
                        type="page"
                    />
                )}
                {tab === 1 && (
                    <Links
                        id={page.id}
                        allLinks={page.links}
                        type="page"
                        linkId={null}
                    />
                )}
            </Box>
        </>
    );
};

export default NewPage;